import { useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SettingsItem from './components/settingsItem/SettingsItem';
import { changePassword, deleteAccount, getUserData, updateUserData } from '@/api/user';
import { saveInfo } from '@/services/user/Actions';
import LanguageSetting from '@/ui/languageSetting';
import lang from '@/assets/lang.svg';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';

function AccountSettings() {
  const user = useSelector((state: any) => state.user?.info);
  const dispatch = useDispatch();
  const [t] = useTranslation('settings');

  const [openAccountModal, setOpenAccountModal] = useState(false);

  const initialValues = {
    name: user?.fullname,
  };
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .max(255, t(`Name must be less than 255 characters.`))
      .required(t(`Name cannot be empty.`)),
  });

  const onSubmit = async (values, { setErrors }) => {
    const updateName = await updateUserData(user.id, { fullname: values.name });
    if (updateName) {
      const updatedUserInfo = await getUserData(user.id);
      dispatch(saveInfo(updatedUserInfo.data));
      toast.success(t('Saved successfully!'));
    } else {
      setErrors({ name: t(`Error!`) });
    }
  };

  const passwordValues = {
    password: '',
    newPassword: '',
    newPasswordCheck: '',
  };

  const passwordValidationSchem = Yup.object().shape({
    newPassword: Yup.string()
      .min(8, t('Password must be at least 8 characters.'))
      .max(16, t('Password must be less than 17 characters.'))
      .matches(/[a-zA-z]/, t('Password must contain at least one letter.'))
      .matches(/[0-9]/, t('Password must contain at least one number.'))
      .required(t('Password cannot be empty.')),
    newPasswordCheck: Yup.string()
      .min(8, t('Password must be at least 8 characters.'))
      .max(16, t('Password must be less than 17 characters.'))
      .matches(/[a-zA-z]/, t('Password must contain at least one letter.'))
      .matches(/[0-9]/, t('Password must contain at least one number.'))
      .required(t('Password cannot be empty.')),
  });

  const onPasswordSubmit = async (values, { setErrors }) => {
    if (values.newPassword !== values.newPasswordCheck)
      return setErrors({ newPasswordCheck: t('Passwords do not match.') });

    const res = await changePassword(values.password, values.newPassword, values.newPasswordCheck);
    if (!res) return setErrors({ newPasswordCheck: t('Error! Please check your password.') });
    toast.success(t('Saved successfully!'));
  };

  const handleAccountDelete = async () => {
    await deleteAccount(user.id, { options: null, details: null });
    logout();
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('state');
    window.location.href = '/signin';
  };

  return (
    <>
      <div className="py-12 space-y-6 md:space-y-12 max-w-2xl mx-auto">
        {/* Profile Settings */}
        <div>
          <div className="text-xl py-4">{t('Profile')}</div>
          <div>
            <div className="space-y-4">
              <SettingsItem title={t('Email')} value={user?.email} disabled={true} />
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ values, errors, handleChange, handleSubmit, touched }) => (
                  <SettingsItem
                    errors={errors.name as string}
                    title={t('Name')}
                    touched={touched.name as boolean}
                    name="name"
                    value={values.name}
                    onClick={handleSubmit}
                    onChange={handleChange}
                  />
                )}
              </Formik>
            </div>
            <Formik
              initialValues={passwordValues}
              validationSchema={passwordValidationSchem}
              onSubmit={onPasswordSubmit}
            >
              {({ values, errors, handleChange, handleSubmit, touched }) => (
                <div className="space-y-4">
                  <SettingsItem
                    errors={errors.password as string}
                    title={t('Password')}
                    touched={touched.password as boolean}
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    type="password"
                  />
                  <div className="flex gap-4">
                    <SettingsItem
                      errors={errors.newPassword as string}
                      title={t('New Password')}
                      touched={touched.newPassword as boolean}
                      name="newPassword"
                      value={values.newPassword}
                      onChange={handleChange}
                      type="password"
                    />
                    <SettingsItem
                      errors={errors.newPasswordCheck as string}
                      title={t('New Password Check')}
                      touched={touched.newPasswordCheck as boolean}
                      name="newPasswordCheck"
                      value={values.newPasswordCheck}
                      type="password"
                      onChange={handleChange}
                      onClick={handleSubmit}
                    />
                  </div>
                </div>
              )}
            </Formik>
          </div>
        </div>
        {/* Language Setttings */}
        <div title={t('Language')} className="flex justify-between">
          <div className="flex items-center gap-2 text-text-400">
            <img src={lang} alt="lang" />
            <span>{t('Language')}</span>
          </div>
          <LanguageSetting />
        </div>
        {/* Delete Account */}
        <Button
          variant="destructive"
          className="text-white ml-auto"
          onClick={() => setOpenAccountModal(true)}
        >
          {t('Delete Account')}
        </Button>
        {/* Delete Account Modal */}
      </div>
      <Transition appear show={openAccountModal} as={Fragment}>
        <Dialog as="div" className="relative" onClose={() => setOpenAccountModal(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" style={{ zIndex: 1000 }} />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 1000 }}>
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="max-w-md w-full transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all p-5">
                  <DialogTitle className="text-lg mb-2">{t('Attention')}</DialogTitle>
                  <div>
                    <div className="text-sm text-text-300">
                      {t('Are you sure you want to delete account?')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-text-300">
                      {t('All the settings and information will be removed.')}
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button
                      variant="destructive"
                      className="ml-auto text-white"
                      onClick={handleAccountDelete}
                    >
                      {t('Delete Account')}
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

export default AccountSettings;
